import { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import EmojiPicker from "emoji-picker-react";
import { useTranslation } from "react-i18next";
import { initSocket, socket } from "socket";
import cx from "classnames";
import Avatar from "react-avatar";
import useWindowSize from "hook/useWindowSize";
import { getListMessages, sendMessage, sendImageMessage } from "actions/messageActions";
import { getListConversations } from "actions/conversationActions";
import Detail from "./sideBarDetail";
import ModalImage from "./modalImage";
import MessageContent from "./messageContent";
import MessageContentBottom from "./messageContentBottom";

const usePrev = (value) => {
    const ref = useRef();

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
};

const MessageContents = ({
    conversation,
    getListMessages,
    setLoading,
    sendMessage,
    getListConversations,
    sendImageMessage,
    settings,
    shopSelected,
}) => {
    const [toggle, setToggle] = useState(false);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [image, setImage] = useState("");
    const [pageMessage, setPageMessage] = useState(1);
    const [totalMessage, setTotalMessage] = useState(0);
    const [scrollPotion, setScrollPosition] = useState(0);
    const [showDetail, setShowDetail] = useState(false);
    const [loadingSend, setLoadingSend] = useState(false);
    const oldConversionId = usePrev(conversation?.conversations_tiktok_id);
    const scrollMessageRef = useRef(null);
    const divRef = useRef(null);
    const inputFileRef = useRef(null);
    const { t } = useTranslation();
    const { height: windowH } = useWindowSize();

    useEffect(() => {
        const { height } = document.getElementById("message-main")?.getBoundingClientRect();
        const { height: headerH } = document.getElementById("kt_chat_messenger_header")?.getBoundingClientRect();
        const { height: footerH } = document.getElementById("kt_chat_messenger_footer")?.getBoundingClientRect() || {
            height: 0,
        };
        document.getElementById("kt_chat_messenger_body_sc").style.maxHeight = `calc(${height - headerH - footerH
            }px - 2rem)`;
    }, [windowH, conversation]);

    useEffect(() => {
        if (conversation) {
            const checkNew = oldConversionId !== conversation.conversations_tiktok_id;
            setLoading(true);
            getListMessages(checkNew ? 1 : pageMessage, 10, conversation.conversations_tiktok_id).then((data) => {
                if (pageMessage === 1 || checkNew) setScrollPosition(-1);
                const dataMessages = data?.listmessages || [];
                setMessages((prev) => (checkNew ? dataMessages : [...dataMessages, ...prev]));
                setTotalMessage(data?.totalsize || 0);
                setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getListMessages, setLoading, pageMessage, conversation]);

    useEffect(() => {
        if (!socket) {
            initSocket();
        }
        socket.removeAllListeners("new_message");
        socket.on("new_message", function (data) {
            if (conversation?.conversations_tiktok_id === data.data.conversation_id) {
                setMessages((prev) => [...prev, data.data]);
                setTotalMessage((prev) => prev + 1);
            }
            getListConversations(1, 10);
        });
    }, [getListConversations, conversation]);

    useEffect(() => {
        if (pageMessage === 1) {
            // divRef?.current?.scrollIntoView({ behaviour: "smooth" });
            scrollMessageRef.current.scrollTop = scrollMessageRef.current.scrollHeight;
        } else {
            if (scrollMessageRef.current) {
                const scrollContainer = scrollMessageRef?.current;
                const oldScrollHeight = scrollContainer?.scrollHeight;
                scrollMessageRef.current.scrollTop = oldScrollHeight - scrollPotion;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, pageMessage]);

    const onClickToggle = () => {
        setToggle(!toggle);
    };

    const handleScroll = (e) => {
        if (e.target.scrollTop === 0) {
            if (totalMessage >= pageMessage * 10) {
                const scrollContainer = scrollMessageRef?.current;
                const oldScrollHeight = scrollContainer?.scrollHeight;
                setScrollPosition(oldScrollHeight);
                setPageMessage(pageMessage + 1);
            }
        }
    };

    const onClickSendMessage = async (mess = "", type = "") => {
        if (loadingSend) return;
        const messageId = Date.now();
        let messageData = {
            conversations_id: conversation?.conversations_tiktok_id,
            messages_tiktok_id: messageId,
            content: JSON.stringify({
                content: mess || message,
            }),
            is_visible: true,
            type: "pending",
            sender: {
                role: "ONCLICK_SEND_MESSAGE"
            }
        };
        setTotalMessage((prev) => prev + 1);
        setMessages((prev) => [...prev, messageData]);
        setLoadingSend(false);
        setScrollPosition(-1);

        setLoadingSend(true);
        const data = await sendMessage({
            conversations_id: conversation?.conversations_tiktok_id,
            message: mess || message,
            setMessage,
            settings,
            type,
        });
        setMessages((prev) => prev.map((m) => {
            if (m.messages_tiktok_id === messageId) {
                if (!data?.data_message) return { ...m, type: "error" }
                return { ...data?.data_message }
            };
            return m
        }));
        setLoadingSend(false);
        setScrollPosition(-1);
        // divRef?.current?.scrollIntoView({ behaviour: "smooth" });
        scrollMessageRef.current.scrollTop = scrollMessageRef.current.scrollHeight;
    };

    const onBtnClick = () => {
        inputFileRef.current.click();
    };

    const onFileChangeCapture = async (e) => {
        if (loadingSend) return;
        if (e.target.files?.length) {
            setLoadingSend(true);
            const data = await sendImageMessage(conversation.conversations_tiktok_id, e.target.files);
            if (data?.data_message) {
                setMessages((prev) => [...prev, data?.data_message]);
                setTotalMessage((prev) => prev + 1);
            }
            setLoadingSend(false);
            setScrollPosition(-1);
            // divRef?.current?.scrollIntoView({ behaviour: "smooth" });
            scrollMessageRef.current.scrollTop = scrollMessageRef.current.scrollHeight;
        }
    };

    const onKeyPress = async (e) => {
        if (e.charCode === 13) {
            e.preventDefault();
            onClickSendMessage();
        }
    };

    const onChange = (e) => {
        setMessage(e.target.value);
    };

    return (
        <>
            <div className="flex-lg-row-fluid">
                <div className="card h-100" id="kt_chat_messenger">
                    <div
                        className="card-header"
                        style={{ backgroundColor: "#f1f3f5", borderBottom: "1px solid #dee2e6" }}
                        id="kt_chat_messenger_header"
                    >
                        {conversation && (
                            <>
                                <div className="card-title">
                                    <div className="d-flex justify-content-center flex-column me-3">
                                        <div className="symbol symbol-45px symbol-circle ">
                                            {conversation?.participants?.[1]?.avatar ? (
                                                <img src={conversation?.participants?.[1]?.avatar} alt="" />
                                            ) : (
                                                <Avatar
                                                    name={conversation?.participants?.[1]?.nickname}
                                                    size="45"
                                                    round={true}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-center flex-column me-3">
                                        <span
                                            role="button"
                                            className="fs-4 fw-bold text-gray-900 text-hover-primary me-1 mb-2 lh-1"
                                            onClick={() => setShowDetail(true)}
                                        >
                                            {conversation?.participants?.[1]?.nickname || t("unknow")}
                                        </span>
                                        <div className="mb-0 lh-1">
                                            <span className="badge badge-success badge-circle w-10px h-10px me-1" />
                                            <span className="fs-7 fw-semibold text-muted">Active</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center">
                                    <h4>GMT-7 (LA)</h4>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="card-body pb-0" style={{ backgroundColor: "#f1f3f5" }} id="kt_chat_messenger_body">
                        <div
                            className="me-n5 pe-5 h-300px h-lg-auto"
                            id="kt_chat_messenger_body_sc"
                            style={{ maxHeight: 436, overflowY: "auto" }}
                            ref={scrollMessageRef}
                            onScroll={(e) => handleScroll(e)}
                        >
                            {messages.map((message, key) =>
                                [
                                    "SYSTEM",
                                    "NOTIFICATION",
                                    "OTHER",
                                    "ALLOCATED_SERVICE",
                                    "BUYER_ENTER_FROM_TRANSFER",
                                ].includes(message?.type) ? (
                                    <div className="text-center mb-4" key={key}>
                                        <MessageContent
                                            data={message}
                                            setLoading={setLoading}
                                            setImage={setImage}
                                            showTrans={false}
                                        />
                                    </div>
                                ) : message?.sender?.role === "BUYER" || !message?.sender?.role ? (
                                    <div key={key} className="d-flex flex-column justify-content-start mb-10 ">
                                        <div className="d-flex align-items-start">
                                            <div className="d-flex mb-2">
                                                <div className="symbol symbol-35px symbol-circle ">
                                                    <img src={message?.sender?.avatar || "https://cdn-icons-png.flaticon.com/512/4762/4762707.png"} alt="" />
                                                </div>
                                                <div className="ms-3">
                                                    {/* <span
                                                    role="button"
                                                    className="fs-5 fw-bold text-gray-900 text-hover-primary me-1"
                                                >
                                                    {message?.sender?.nickname}
                                                </span> */}
                                                </div>
                                            </div>
                                            <div>
                                                <div
                                                    className="p-5 rounded bg-white text-gray-900 fw-semibold mw-lg-400px text-start"
                                                    data-kt-element="message-text"
                                                    style={{ opacity: message?.type === "pending" ? "0.5" : "1" }}
                                                >
                                                    <MessageContent
                                                        data={message}
                                                        setLoading={setLoading}
                                                        setImage={setImage}
                                                    />
                                                </div>
                                                <p className="text-muted fs-7 mt-2"><MessageContentBottom message={message} /></p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div key={key} className="d-flex justify-content-end mb-10 ">
                                        <div className="d-flex flex-column align-items-end">
                                            <div className="d-flex">
                                                <div>
                                                    <div
                                                        className="p-5 rounded bg-light-primary text-gray-900 fw-semibold mw-lg-400px text-end"
                                                        data-kt-element="message-text"
                                                        style={{ opacity: message?.type === "pending" ? "0.5" : "1" }}
                                                    >
                                                        <MessageContent
                                                            data={message}
                                                            setLoading={setLoading}
                                                            setImage={setImage}
                                                        />
                                                    </div>
                                                    <p className="text-muted fs-7 mt-2 text-end">
                                                        <MessageContentBottom message={message} />
                                                    </p>
                                                </div>
                                                <div className="d-flex mb-2 ms-3">
                                                    <div className="symbol  symbol-35px symbol-circle ">
                                                        <img
                                                            alt=""
                                                            src="https://cdn-icons-png.flaticon.com/512/8771/8771926.png"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}
                            <div ref={divRef}></div>
                        </div>
                    </div>
                    {conversation?._id && (
                        <div className="card-footer pt-4" id="kt_chat_messenger_footer" style={{ minHeight: "136px" }}>
                            <textarea
                                className="form-control form-control-flush mb-3"
                                rows="1"
                                data-kt-element="input"
                                placeholder="Type a message... Enter to send..."
                                name="message"
                                onChange={(e) => {
                                    onChange(e);
                                }}
                                value={message}
                                onKeyPress={(e) => {
                                    onKeyPress(e);
                                }}
                            ></textarea>
                            <div className="d-flex flex-stack">
                                <div className="d-flex align-items-center me-2">
                                    <button
                                        className="btn btn-sm btn-icon btn-active-light-primary me-1"
                                        type="button"
                                        onClick={onBtnClick}
                                        disabled={loadingSend}
                                    >
                                        <label>
                                            <i className="ki-outline ki-paper-clip fs-3 cursor-pointer"></i>
                                        </label>
                                    </button>
                                    <input
                                        type="file"
                                        className="d-none"
                                        ref={inputFileRef}
                                        onChangeCapture={onFileChangeCapture}
                                    />
                                    <button
                                        className="btn btn-sm btn-icon btn-active-light-primary me-1"
                                        type="button"
                                        onClick={() => onClickToggle()}
                                    >
                                        <label>
                                            <span className="cursor-pointer">😀</span>
                                        </label>
                                    </button>
                                    <div className={cx("al-emo-css", { "d-none": !toggle })}>
                                        <EmojiPicker
                                            searchDisabled="true"
                                            previewConfig={{ showPreview: false }}
                                            emojiStyle="facebook"
                                            onEmojiClick={(e) => {
                                                setToggle(false);
                                                setMessage((prev) => prev + e.emoji);
                                            }}
                                        />
                                    </div>
                                    {settings?.autoTranslate && (
                                        <button
                                            className="btn btn-sm btn-icon btn-active-light-primary me-1"
                                            type="button"
                                            title="Auto Translate"
                                        >
                                            <i className="fa fa-lightbulb"></i>
                                        </button>
                                    )}
                                </div>
                                <button
                                    onClick={() => onClickSendMessage()}
                                    className="btn btn-primary"
                                    type="button"
                                    data-kt-element="send"
                                    disabled={loadingSend}
                                >
                                    {t("Send")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showDetail && (
                <Detail
                    conversation={conversation}
                    onClose={() => setShowDetail(false)}
                    reloadConversation={() => getListConversations(1, 10)}
                    send={onClickSendMessage}
                    shopSelected={shopSelected}
                />
            )}
            <ModalImage url={image} onToggle={() => setImage("")} />
        </>
    );
};

const mapStateToProps = (state) => ({});
const mapDispatchToProps = {
    getListMessages,
    sendMessage,
    sendImageMessage,
    getListConversations,
};

export default connect(mapStateToProps, mapDispatchToProps)(MessageContents);
